/* eslint-disable react/jsx-handler-names */
import React, { useState, useRef, useEffect } from 'react';

import classNames from 'classnames';
import Loader from '../Loader';

import Badge from '../Badge';
import Transition from '../Transition';
import Action from './Action';

export default function ActionsMenu({
  align = 'right',
  badgeColor,
  badgeText,
  actions,
  isLoading,
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const trigger = useRef(null);
  const dropdown = useRef(null);

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  return (
    <div className="relative inline-flex">
      <button
        type="button"
        ref={trigger}
        className={classNames(
          `flex items-center justify-center bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600/80 rounded-full text-slate-500`,
          {
            'bg-slate-200': dropdownOpen,
            'pr-2 pl-1': badgeText || isLoading,
            'w-8 h-8': !badgeText && !isLoading,
          }
        )}
        aria-haspopup="true"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
      >
        {isLoading && (
          <div className="mr-1">
            <Loader />
          </div>
        )}
        <span className="sr-only">Notifications</span>
        {badgeText && <Badge color={badgeColor} text={badgeText} />}
        <svg
          className="w-6 h-6 fill-current"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          strokeLinecap="butt"
          xmlns="http://www.w3.org/2000/svg"
        >
          <line x1="4" y1="6" x2="20" y2="6" />
          <line x1="4" y1="12" x2="20" y2="12" />
          <line x1="4" y1="18" x2="20" y2="18" />
        </svg>
      </button>

      <Transition
        className={`origin-top-right z-10 absolute top-full -mr-48 sm:mr-0 min-w-80 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 py-1.5 rounded shadow-lg overflow-hidden mt-1 ${
          align === 'right' ? 'right-0' : 'left-0'
        }`}
        show={dropdownOpen}
        enter="transition ease-out duration-200 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-200"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
      >
        <div
          ref={dropdown}
          onFocus={() => setDropdownOpen(true)}
          onBlur={() => setDropdownOpen(false)}
        >
          <div className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase pt-1.5 pb-2 px-4">
            Actions
          </div>
          <ul>
            {actions?.map((action) => {
              return (
                <Action
                  key={action.actionTitle}
                  actionTitle={action.actionTitle}
                  actionDescription={action.actionDescription}
                  icon={action.icon}
                  onClick={action.onClick}
                  handleClose={() => setDropdownOpen(false)}
                  disabled={action.disabled}
                />
              );
            })}
          </ul>
        </div>
      </Transition>
    </div>
  );
}
